import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useChat } from '@/hooks/useChat';
import { completeOnboarding } from '@/lib/onboarding-api';
import { ChatInterface } from './ChatInterface';

interface OnboardingFlowProps {
  onComplete: () => void;
  onSkip?: () => void;
}

export function OnboardingFlow({ onComplete, onSkip }: OnboardingFlowProps) {
  const [isCompleting, setIsCompleting] = useState(false);
  const { messages, isLoading, error, sendMessage, startNewSession, clearError } = useChat();

  // Start onboarding session with onboarding agent on mount
  useEffect(() => {
    // Find onboarding agent ID - typically 'agent_onboarding_001'
    // For now, we'll let the chat pick it up from the welcome screen
    startNewSession();
  }, [startNewSession]);

  async function handleComplete() {
    setIsCompleting(true);
    try {
      // Extract preferences from conversation if possible
      // For now, just mark as complete
      await completeOnboarding();
      onComplete();
    } catch (err) {
      console.error('Failed to complete onboarding:', err);
    } finally {
      setIsCompleting(false);
    }
  }

  async function handleSkip() {
    if (onSkip) {
      onSkip();
    } else {
      // Skip = complete without preferences
      await completeOnboarding();
      onComplete();
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 20 }}
          className="w-full max-w-4xl h-[85vh] bg-charcoal-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-charcoal-700">
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-red-500 via-amber-500 to-blue-400 bg-clip-text text-transparent font-heading">
                Welcome to LevelUp Coach!
              </h2>
              <p className="text-slate-400 text-sm mt-1">
                Let's get to know you and customize your coaching experience
              </p>
            </div>
            {onSkip && (
              <button
                onClick={handleSkip}
                className="text-slate-400 hover:text-white transition-colors p-2"
                title="Skip onboarding"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Chat Interface */}
          <div className="flex-1 overflow-hidden p-6">
            <ChatInterface
              messages={messages}
              onSendMessage={sendMessage}
              isLoading={isLoading}
              error={error}
              onClearError={clearError}
            />
          </div>

          {/* Footer with Complete Button */}
          <div className="p-6 border-t border-charcoal-700 bg-charcoal-800/50">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-slate-400">
                Chat with our Onboarding Guide to help us understand your goals and preferences
              </p>
              <div className="flex gap-3">
                {onSkip && (
                  <button
                    onClick={handleSkip}
                    className="px-6 py-2 rounded-xl bg-charcoal-700 text-white hover:bg-charcoal-600 transition-colors"
                  >
                    Skip for now
                  </button>
                )}
                <button
                  onClick={handleComplete}
                  disabled={isCompleting || messages.length < 2}
                  className="px-6 py-2 rounded-xl bg-royal-gradient text-white font-medium
                           hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed
                           transition-all shadow-glow"
                >
                  {isCompleting ? 'Completing...' : 'Complete Onboarding'}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
